import request from '@teetsh/app/src/utils/request';
import { saveAs } from 'file-saver';
import { Sequence, FDPPDFSettings } from '@teetsh/app/src/types/sequences';
import { useState } from 'react';
import { useFieldsToDisplay } from 'containers/Sequences/Preview/Sidebar/FieldsToDisplay/atom';
import { useTranslation } from 'react-i18next';
import useNotification from 'containers/App/Notifications/useNotification';
import { PdfResponse } from 'types/pdf';
import { buildFDPPDFParams } from 'containers/Sequences/Preview/Sidebar/buildFDPPDFParams';
import * as Sentry from '@sentry/react';

interface Props {
  userId?: string;
  sequenceId: string;
  sequenceTitle: string;
  settings?: FDPPDFSettings;
  fieldsToDisplay?: string[];
  onStart: () => void;
  onFinished: () => void;
  onError: () => void;
}

const fetchSequenceAPI = (s3Url: string, sequenceTitle: string) => {
  const fileName = sequenceTitle.replace(/ /g, '_');

  return fetch(s3Url)
    .then(response => response.blob())
    .then(response => {
      saveAs(response, `${fileName}.pdf`);
    })
    .catch(err => {
      // eslint-disable-next-line no-console
      console.error('Error saving sequence pdf', err);
      throw err;
    });
};

export default async function downloadSequence(props: Props) {
  const {
    sequenceId,
    sequenceTitle,
    onStart,
    onFinished,
    onError,
    userId,
    ...params
  } = props;

  onStart();

  const queryParams = buildFDPPDFParams(params);
  const requestURL = userId
    ? `/users/${userId}/sequences/${sequenceId}/generate?${queryParams.toString()}`
    : `/sequences/${sequenceId}/generate?${queryParams.toString()}`;
  try {
    const response: PdfResponse = await request(requestURL, { method: 'GET' });
    const token = response.pdf_token;
    await fetchSequenceAPI(token, sequenceTitle);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('Error downloading sequence', err);
    Sentry.captureException(err, {
      extra: { requestURL, sequenceId, sequenceTitle },
    });
    onError();
  } finally {
    onFinished();
  }
}

export function useDownloadSequence() {
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const fieldsToDisplay = useFieldsToDisplay();
  const { notifyError } = useNotification();
  const { t } = useTranslation(['common']);

  const download = ({
    sequence,
    ...params
  }: {
    sequence: Sequence;
    settings?: FDPPDFSettings;
    ficheDePrepId?: string;
    fieldsToDisplay?: string[];
  }) =>
    downloadSequence({
      sequenceId: sequence.id,
      sequenceTitle: sequence.title,
      fieldsToDisplay,
      ...params,
      onStart: () => setIsDownloading(true),
      onFinished: () => setIsDownloading(false),
      onError: () => notifyError({ title: t('common:messages.genericError') }),
    });

  return {
    download,
    isDownloading,
  };
}
